import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';

import fetchMissingToken from 'actions/fetchMissingToken.action';
import fetchSalon from 'actions/fetchSalon.action';
import listSalons from 'actions/listSalons.action';
import postPin from 'actions/pin/postPin.action';
import PinForm from 'components/core/PinForm.component';
import selectIsLoggedIn from 'selectors/selectIsLoggedIn.selector';
import selectLocked from 'selectors/selectLocked';
import selectLockEnabled from 'selectors/selectLockEnabled';
import selectSupportedSalons from 'selectors/selectSupportedSalons';
import { selectLatestUiActionTime, selectLockToken } from 'selectors/simple';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import { isEnvTest } from 'utils/env/determine.util';
import logger from 'utils/logger.util';
import checkSupportSalon from 'utils/salons/checkSupportSalon';

import { uiSetLock } from 'lib/actions';
import checkLock from 'lib/checkLock';

const L = logger('PinWatcher');

const TIMEOUT = Object.freeze({
  immediate: 0,
  quick: 1000,
  normal: 5000,
});

class PinWatcher extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { timeoutId: null };
  }

  componentDidMount() {
    this.refreshTimeout();
  }

  // noinspection JSCheckFunctionSignatures
  componentDidUpdate({ latest, locked, hasToken }) {
    const { hasToken: oldHasToken, latest: oldLatest, locked: oldLocked } = this.props;
    if (latest !== oldLatest || locked !== oldLocked || hasToken !== oldHasToken) {
      this.refreshTimeout();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.state.timeoutId);
  }

  lock = () => void this.props.uiSetLock(true);
  handleOnPin = (pin) => void this.props.postPin({ pin, salonId: fromAnyToInteger(this.props.match.params.id) });
  scheduleRecheck = (timeout) => void setTimeout(() => void this.refreshTimeout(), timeout);

  refreshTimeout = () => {
    const { latest, threshold, lockEnabled, supportSalons, hasToken } = this.props;
    const salonId = fromAnyToInteger(this.props.match.params.id);
    const support = checkSupportSalon({ salons: supportSalons, salonId });

    if (support === null) {
      // null is the initial value, no true/false means no info from BE yet, ask for fresh data and schedule a recheck
      if (this.props.isLoggedIn) {
        // try to refresh access to salons only if logged in
        this.props.listSalons();
      }

      return void this.scheduleRecheck(TIMEOUT.quick);
    }

    if (!hasToken || lockEnabled === null) {
      this.props.fetchMissingToken({ salonId, support });
      return void this.scheduleRecheck(TIMEOUT.quick);
    }

    if (support === true) {
      // this check will bail out even if lockEnabled is true
      return void clearTimeout(this.state.timeoutId);
    }

    const { auto, diff, locked, future, error } = checkLock({ latest, threshold, now: Date.now() });
    if (error) return void L.warn('refreshTimeout()', error);

    if (this.state.timeoutId) {
      // remove any previous timeout, then decide if a new one is needed
      clearTimeout(this.state.timeoutId);
    }

    if (lockEnabled !== true) {
      return void this.scheduleRecheck(
        // no true/false means no info from BE yet, in that case schedule immediate recheck
        lockEnabled === true || lockEnabled === false ? TIMEOUT.normal : TIMEOUT.immediate,
      );
    }

    // just a simple handler that when time's up it switches state to locked
    // if it gets cleared before fired, will most likely be replaced with a new one
    const handler = () => {
      L.info('refreshTimeout()', 'handler()', 'Lockout time has been reached, locking access...');
      this.lock();
    };

    if (locked || !hasToken) return void this.setState({ timeoutId: setTimeout(handler, 0) });

    if (!auto) return void 1;
    if (!future) return void this.scheduleRecheck(TIMEOUT.normal);

    // needed for the clear above
    return void this.setState({ timeoutId: setTimeout(handler, diff) });
  };

  render() {
    // FIXME: write better tests that don't depend on BE to make this one go away
    const notTestingEnv = !isEnvTest();
    const { locked, hasToken, lockEnabled } = this.props;

    L.debug('render()', 'Empty because:', { lockEnabled, locked, hasToken, notTestingEnv });

    if (locked !== true && (lockEnabled !== true || hasToken)) return null;

    return <PinForm onPin={this.handleOnPin} />;
  }
}

const PinWatcherHoc = withRouter(
  connect(
    (state) => ({
      hasToken: !!selectLockToken(state),
      isLoggedIn: selectIsLoggedIn(state),
      latest: selectLatestUiActionTime(state),
      locked: selectLocked(state),
      lockEnabled: selectLockEnabled(state),
      supportSalons: selectSupportedSalons(state),
      threshold: state?.lib?.threshold,
    }),
    (dispatch) =>
      bindActionCreators(
        {
          uiSetLock,
          postPin,
          fetchSalon,
          fetchMissingToken,
          listSalons,
        },
        dispatch,
      ),
  )(PinWatcher),
);

export default PinWatcherHoc;
